import React, { useState } from 'react';
import { Upload, Loader2, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';

const ImageUpload = ({ value, onChange, label, className = '' }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [showUrlInput, setShowUrlInput] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload to backend
      const response = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url;
      setPreviewUrl(imageUrl);
      onChange(imageUrl);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlSubmit = () => {
    if (previewUrl) {
      onChange(previewUrl);
      setShowUrlInput(false);
      toast.success('Image URL saved!');
    }
  };

  const handleRemove = () => {
    setPreviewUrl('');
    onChange('');
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && (
        <Label className="text-xs uppercase tracking-widest">{label}</Label>
      )}

      {/* Preview */}
      {previewUrl && (
        <div className="relative inline-block">
          <img 
            src={previewUrl} 
            alt="Preview" 
            className="w-full max-w-md h-48 object-cover rounded-xl border-2 border-border"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 rounded-full"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Upload Options */}
      {!previewUrl && (
        <div className="space-y-3">
          {/* File Upload Button */}
          <div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
              id={`file-upload-${label}`}
              disabled={uploading}
            />
            <label htmlFor={`file-upload-${label}`}>
              <Button
                type="button"
                variant="outline"
                className="rounded-full w-full"
                disabled={uploading}
                asChild
              >
                <span className="cursor-pointer flex items-center justify-center gap-2">
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      Upload from Computer
                    </>
                  )}
                </span>
              </Button>
            </label>
          </div>

          {/* OR Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">Or</span>
            </div>
          </div>

          {/* URL Input Toggle */}
          {!showUrlInput ? (
            <Button
              type="button"
              variant="ghost"
              className="rounded-full w-full"
              onClick={() => setShowUrlInput(true)}
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Use Image URL
            </Button>
          ) : (
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={previewUrl}
                onChange={(e) => setPreviewUrl(e.target.value)}
                className="rounded-full flex-1"
              />
              <Button
                type="button"
                onClick={handleUrlSubmit}
                className="rounded-full"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowUrlInput(false)}
                className="rounded-full"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}

      {/* File Info */}
      <p className="text-xs text-muted-foreground">
        Max file size: 10MB. Supported: JPG, PNG, WebP, GIF
      </p>
    </div>
  );
};

export default ImageUpload;
